import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { HelperFunctions } from 'helpers';
import { Popover, Button, Menu, MenuItem, Position, Intent, Icon } from "@blueprintjs/core";
import "./AssetTypeFilterRenderer.css";

export function AssetTypeFilterRenderer({
    filter,
    onItemSelect,
    isItemSelected = () => false,
    expandedAssetTypes = {},
    setExpandedAssetTypes = () => { },
    selectedOptions = [],
    clearSelectedForFilter
}) {

    useEffect(() => {
        if (!filter?.items) return;

        let shouldUpdate = false;
        const expanded = { ...expandedAssetTypes };

        filter.items.forEach(type => {
            const subtypes = type.subtypes || [];
            if (subtypes.length > 0) {
                const isSubtypeSelected = subtypes.some(sub =>
                    isItemSelected("AssetTypeSubtype", sub.id)
                );
                const isUncategorisedSelected = isItemSelected("AssetTypeSubtype", `uncat-${type.id}`);
                if ((isSubtypeSelected || isUncategorisedSelected) && !expanded[type.id]) {
                    expanded[type.id] = true;
                    shouldUpdate = true;
                }
            }
        });

        if (shouldUpdate) {
            setExpandedAssetTypes(expanded);
        }

    }, [filter, selectedOptions]);

    function isAssetTypeSubtypeSelected(name, id, parentAssetTypeId) {
        if (!selectedOptions) return false;

        return selectedOptions.some(option => {
            if (option.name !== name || option.value !== id) {
                return false;
            }
            if (name === "AssetTypeSubtype" && typeof id === "string" && id.startsWith("uncat-")) {
                return option.parentAssetTypeId === parentAssetTypeId;
            }
            return true;
        });
    }

    function setFilterText() {
        if (selectedOptions.length === 0) {
            return filter.displayName != null ? filter.displayName : filter.name;
        }

        var filterSelectedOptions = selectedOptions.filter(function (instance) {
            //search subtypes
            for (const type of filter.items) {
                if (type.name === instance.displayName) {
                    return true;
                }
                if (type.subtypes && Array.isArray(type.subtypes)) {
                    for (const subtype of type.subtypes) {
                        if (subtype.name === instance.displayName) {
                            return true;
                        }
                    }
                }
            }

            return instance.name.toLowerCase() === filter.name.toLowerCase() && instance.value > 0;
        });

        switch (filterSelectedOptions.length) {
            case 0:
                return 'Select filter';
            case 1:
                return filterSelectedOptions[0].displayName;
            default:
                return `${filterSelectedOptions[0].displayName}, (+${filterSelectedOptions.length - 1} more)`;
        }
    }

    return (
        
        <div>
            <div className="filter-header">{filter.displayName}</div>
            <div className="filter-popover-container">
            <div className="dropdown-renderer">
                <Popover
                    key={filter.name}
                    minimal
                    position={Position.BOTTOM_LEFT}
                    popoverClassName="asset-type-filter-popover"
                    content={
                        <Menu>
                            {filter.items.map(type => {
                                let subtypes = type.subtypes || [];
                                let subtypeItems = [];
                                if (subtypes.length > 0) {
                                    subtypeItems = subtypes.map(sub => {
                                        if (sub.name === "Uncategorised") {
                                            return {
                                                ...sub,
                                                parentAssetTypeId: type.id,
                                                id: `uncat-${type.id}`
                                            };
                                        }
                                        return sub;
                                    });
                                }

                                return (
                                    <React.Fragment key={type.id}>
                                        <MenuItem
                                            text={
                                                <span
                                                    className="menu-item-parent"
                                                    onClick={subtypes.length > 0
                                                        ? e => {
                                                            e.stopPropagation();
                                                            setExpandedAssetTypes((prev) => ({
                                                                ...prev,
                                                                [type.id]: !prev[type.id]
                                                            }));
                                                        }
                                                        : undefined}
                                                    onKeyDown={() => { } }
                                                >
                                                    <span style={{ flex: 1 }}>{type.name}</span>
                                                        {subtypes.length > 0 ? (
                                                            <Icon icon="chevron-down" className="menu-item-parent-icon" />
                                                        ) : null}
                                                </span>
                                            }

                                            icon={
                                                subtypes.length === 0 && isItemSelected && isItemSelected(filter.name, type.id)
                                                ? "tick"
                                                : <span className="asset-type-filter-empty-icon"></span>
                                            }

                                            onClick={
                                                subtypes.length > 0
                                                ? e => e.stopPropagation()
                                                : (e) => {
                                                    e.stopPropagation();
                                                    onItemSelect(filter.name, type)
                                                }
                                                } />

                                            {subtypeItems.length > 0 && expandedAssetTypes[type.id] && (
                                                subtypeItems.map(sub => (
                                                    <MenuItem
                                                        key={sub.id}
                                                        text={<span className="spacer-left">{sub.name}</span>}
                                                        icon={isAssetTypeSubtypeSelected("AssetTypeSubtype", sub.id, sub.parentAssetTypeId)
                                                            ? "tick"
                                                            : <span className="asset-type-filter-empty-icon"></span>}

                                                        onClick={e => {
                                                            //Don't close the popover on click
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onItemSelect("AssetTypeSubtype", sub);
                                                        }}
                                                    />
                                                ))
                                            )}
                                    </React.Fragment>
                                );
                            })}
                        </Menu>
                    }
                >
                    <div className="filter-button-bordered">
                        <Button
                            className="filter-button"
                            text={setFilterText()}
                            endIcon="chevron-down"
                            variant="minimal"
                            id={`filter-${HelperFunctions.kebabCase(filter.name)}-btn`}
                        />
                    </div>
                </Popover>
            </div>
            
            <Button
                icon="cross"
                variant="minimal"
                intent={Intent.NONE}
                onClick={() => clearSelectedForFilter([filter.name, "AssetTypeSubtype"])}
                aria-label={`Clear ${filter.displayName} filter`}
            />    
            </div>                
        </div>
    );
}

AssetTypeFilterRenderer.propTypes = {
    filter: PropTypes.shape({
        name: PropTypes.string.isRequired,
        displayName: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                name: PropTypes.string.isRequired,
                subtypes: PropTypes.array
            })
        ).isRequired
    }).isRequired,
    onItemSelect: PropTypes.func.isRequired,
    isItemSelected: PropTypes.func,
    expandedAssetTypes: PropTypes.object,
    setExpandedAssetTypes: PropTypes.func,
    selectedOptions: PropTypes.array,
    clearSelectedForFilter: PropTypes.func.isRequired
};